import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatChipsModule } from '@angular/material/chips';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { StateManagementService } from '../../services/state-management.service';
import { OnboardingApiService } from '../../services/onboarding-api.service';
import { finalize } from 'rxjs/operators';

export interface UploadedFile {
  name: string;
  url: string;
  type: string;
  size: number;
}

@Component({
  selector: 'app-document-upload',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatChipsModule,
  ],
  templateUrl: './document-upload.component.html',
  styleUrl: './document-upload.component.scss',
})
export class DocumentUploadComponent implements OnInit {
  @Input() organizationId!: string;
  @Output() stepComplete = new EventEmitter<string[]>();

  uploadedFiles: UploadedFile[] = [];
  uploadProgress: { [key: string]: number } = {};
  isUploading = false;
  isSaving = false;
  dragOver = false;

  readonly MAX_FILE_SIZE_MB = 10;
  readonly ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

  constructor(
    private storage: AngularFireStorage,
    private api: OnboardingApiService,
    private stateService: StateManagementService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    // Pre-populate if already uploaded in this session
    const state = this.stateService.snapshot;
    if (state.documentsComplete && state.uploadedDocUrls.length > 0) {
      // Already done in this session
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(): void {
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
    const files = event.dataTransfer?.files;
    if (files) this.handleFiles(Array.from(files));
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(Array.from(input.files));
      input.value = '';
    }
  }

  handleFiles(files: File[]): void {
    for (const file of files) {
      if (!this.ACCEPTED_TYPES.includes(file.type)) {
        this.snackBar.open(`${file.name}: unsupported file type. Use JPG, PNG, or PDF.`, 'Close', { duration: 4000 });
        continue;
      }
      if (file.size > this.MAX_FILE_SIZE_MB * 1024 * 1024) {
        this.snackBar.open(`${file.name}: file too large (max ${this.MAX_FILE_SIZE_MB} MB).`, 'Close', { duration: 4000 });
        continue;
      }
      this.uploadFile(file);
    }
  }

  uploadFile(file: File): void {
    this.isUploading = true;
    const path = `Organizations/${this.organizationId}/documents/${Date.now()}_${file.name}`;
    const ref = this.storage.ref(path);
    const task = this.storage.upload(path, file);

    this.uploadProgress[file.name] = 0;

    task.percentageChanges().subscribe(pct => {
      this.uploadProgress[file.name] = Math.round(pct ?? 0);
    });

    task.snapshotChanges().pipe(
      finalize(async () => {
        const url = await ref.getDownloadURL().toPromise();
        this.uploadedFiles.push({ name: file.name, url, type: file.type, size: file.size });
        delete this.uploadProgress[file.name];
        this.isUploading = Object.keys(this.uploadProgress).length > 0;
      })
    ).subscribe({ error: (err) => {
      this.snackBar.open('Upload failed: ' + err.message, 'Close', { duration: 5000 });
      delete this.uploadProgress[file.name];
      this.isUploading = Object.keys(this.uploadProgress).length > 0;
    }});
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
  }

  async onContinue(): Promise<void> {
    this.isSaving = true;
    const urls = this.uploadedFiles.map(f => f.url);

    try {
      await this.api.markDocumentsComplete(this.organizationId, urls).toPromise();
      this.stateService.setDocumentsComplete(urls);
      this.snackBar.open('Documents saved!', 'Close', { duration: 3000 });
      this.stepComplete.emit(urls);
    } catch (err: any) {
      this.snackBar.open('Error saving documents: ' + (err.message || err), 'Close', { duration: 5000 });
    } finally {
      this.isSaving = false;
    }
  }

  skipStep(): void {
    this.stateService.setDocumentsComplete([]);
    this.stepComplete.emit([]);
  }

  get uploadingKeys(): string[] {
    return Object.keys(this.uploadProgress);
  }

  formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
